import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV_SECTIONS, AGENT_NAV_SECTIONS, AGENT_JOB_SIDE_NAV_SECTIONS } from "./navConfig.js";
import { isJobDemandSide } from "../workerLine/workerLineShared.js";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import "./sidebar.css";

function matchesPath(pathname, path) {
  // The Dashboard item's own `to` ("/backoffice") is the app's shared root
  // path - every other authenticated route is nested under it. Prefix-
  // matching that root the same way as every other item would make
  // Dashboard show active on literally every backoffice page (P127 audit
  // finding), not just the dashboard itself, so it gets an exact-match-only
  // rule here. Every other item's `to` names a real subsection (e.g.
  // "/backoffice/super-agents"), where prefix-matching is exactly what
  // keeps that item active on its own nested detail routes.
  if (path === "/backoffice") {
    return pathname === path;
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * True when the current URL matches this item's own `to`, or any of its
 * optional `activeMatch` paths - the latter lets one nav item (e.g. the
 * Agent Portal's "Dashboard" item, still highlighted on the legacy
 * /backoffice/agent/dashboard redirect URL) highlight as active on a
 * second, related real screen without changing where the link itself
 * navigates to. Most items have no `activeMatch` at all - behavior for
 * every item other than the ones that declare it is unchanged.
 */
function isActivePath(pathname, item) {
  if (matchesPath(pathname, item.to)) {
    return true;
  }
  return Array.isArray(item.activeMatch) && item.activeMatch.some((path) => matchesPath(pathname, path));
}

/** Human label for the real accessScopes[].scopeType field - not invented data, just a readable translation of it. */
function deriveScopeLabel(accessScopes) {
  if (!Array.isArray(accessScopes) || accessScopes.length === 0) {
    return null;
  }
  const hasPlatformScope = accessScopes.some((scope) => scope.scopeType === "Platform");
  return hasPlatformScope ? "All Lines" : "Assigned Scope";
}

function sectionContainsActiveRoute(section, pathname) {
  return section.items.some((item) => isActivePath(pathname, item));
}

// Prompt 141R14 Part A: sections that must render open on first paint,
// independent of any stored collapse preference - COMMAND is SuperAdmin's
// primary work surface, AGENT + SAFETY are an Agent's. The section actually
// holding the current route is force-opened too (see sectionIsOpen below) -
// that rule applies to every role, not just these two, so it isn't in
// either set.
const SUPERADMIN_FORCE_OPEN_TITLES = new Set(["COMMAND"]);
const AGENT_FORCE_OPEN_TITLES = new Set(["AGENT", "SAFETY"]);
const AGENT_JOB_SIDE_FORCE_OPEN_TITLES = new Set(["AGENT"]);

function storageKeyForRole(isAgentPortal) {
  return isAgentPortal ? "wmx-nav-state-agent" : "wmx-nav-state-superadmin";
}

/**
 * Reads { [sectionTitle]: boolean } from localStorage - `false` means the
 * visitor explicitly collapsed that section on a previous visit; a title
 * that's absent (including every title on a first-ever visit, before this
 * key has ever been written) defaults to open. localStorage can throw
 * (private browsing, quota, disabled entirely) - this must never crash the
 * sidebar, so any failure just falls back to "nothing stored yet".
 */
function readStoredSectionState(storageKey) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredSectionState(storageKey, state) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // Best-effort persistence only - the accordion still works for the
    // rest of this session even if it can't be saved (private browsing,
    // storage quota, localStorage disabled by policy).
  }
}

/**
 * Persistent left navigation (desktop) / horizontal nav rail (mobile, via
 * CSS only - same markup, no JS branching). Active state is derived purely
 * from the current URL, matching the rest of this app's URL-first
 * navigation model.
 *
 * Prompt 141R14 Part A: desktop sections are a real accordion, not a flat
 * stack of always-expanded groups - collapse state persists per role in
 * localStorage (`wmx-nav-state-superadmin` / `wmx-nav-state-agent`, never
 * shared between the two, since the two roles see entirely different
 * sections). A section renders open when ANY of these is true: it's one of
 * this role's permanently-emphasized sections (COMMAND for SuperAdmin;
 * AGENT and SAFETY for Agent), it's the section actually holding the
 * current route (so navigating somewhere never hides the very item that
 * shows you're there, even if you'd previously collapsed it), or the
 * visitor simply hasn't collapsed it. Collapsed sections never render their
 * items into the DOM at all (not a CSS-hidden block) - a real accordion,
 * not a decorative animation on top of an always-present list. Mobile (the
 * horizontal rail below 960px, unchanged from before this packet) has no
 * per-section header to click - see sidebar.css's own mobile media query -
 * so an item only disappears from the mobile rail if its section was
 * explicitly collapsed during an earlier desktop-width visit.
 */
export default function Sidebar() {
  const location = useLocation();
  const { status, data } = useBackofficeIdentity();
  const scopeLabel = status === "ready" ? deriveScopeLabel(data.accessScopes) : null;
  // Agent Portal nav (W43) only for a confirmed primaryRole === "Agent" -
  // any other role, or identity still loading/errored, keeps the existing
  // SuperAdmin-style NAV_SECTIONS so nothing flashes the wrong menu.
  const isAgentPortal = status === "ready" && data.primaryRole === "Agent";
  // P156: a job-side Agent (ฝั่งงาน) has no workers, so its menu is the
  // employer side of the product with no SAFETY section at all - see
  // AGENT_JOB_SIDE_NAV_SECTIONS. The side comes from /api/backoffice/home,
  // which the shell already fetches once per guarded page view, so this
  // needs no extra request. Defaults to the worker side while identity is
  // still loading, so the menu never flickers into the smaller job-side one
  // and back.
  const isJobSideAgent = isAgentPortal && isJobDemandSide(data.organizationSide);
  const sections = isAgentPortal ? (isJobSideAgent ? AGENT_JOB_SIDE_NAV_SECTIONS : AGENT_NAV_SECTIONS) : NAV_SECTIONS;
  // The job-side menu has no SAFETY section to force open.
  const forceOpenTitles = isAgentPortal
    ? (isJobSideAgent ? AGENT_JOB_SIDE_FORCE_OPEN_TITLES : AGENT_FORCE_OPEN_TITLES)
    : SUPERADMIN_FORCE_OPEN_TITLES;
  const storageKey = storageKeyForRole(isAgentPortal);

  const [collapsedTitles, setCollapsedTitles] = useState(() => new Set());

  // Re-sync from localStorage whenever the effective role changes (identity
  // resolving from "loading" to a confirmed SuperAdmin/Agent flips
  // storageKey exactly once per session in practice).
  useEffect(() => {
    const stored = readStoredSectionState(storageKey);
    const collapsed = new Set(Object.keys(stored).filter((title) => stored[title] === false));
    setCollapsedTitles(collapsed);
  }, [storageKey]);

  function toggleSection(title) {
    setCollapsedTitles((previous) => {
      const next = new Set(previous);
      const collapsing = !next.has(title);
      if (collapsing) {
        next.add(title);
      } else {
        next.delete(title);
      }
      const stored = readStoredSectionState(storageKey);
      stored[title] = !collapsing;
      writeStoredSectionState(storageKey, stored);
      return next;
    });
  }

  function sectionIsOpen(section) {
    if (forceOpenTitles.has(section.title) || sectionContainsActiveRoute(section, location.pathname)) {
      return true;
    }
    return !collapsedTitles.has(section.title);
  }

  return (
    <aside className="wx-sidebar">
      <div className="wx-sidebar-brand">
        <div className="wx-sidebar-mark" aria-hidden="true">
          WX
        </div>
        <div className="wx-sidebar-brand-text">
          <strong>Workmate X</strong>
          <span>{isAgentPortal ? "Agent Portal" : "Backoffice"}</span>
        </div>
      </div>

      <nav className="wx-sidebar-nav" aria-label="เมนูหลักของระบบ">
        {sections.map((section) => {
          const open = sectionIsOpen(section);
          const panelId = `wx-sidebar-panel-${section.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
          return (
            <div className="wx-sidebar-section" key={section.title}>
              <button
                type="button"
                className="wx-sidebar-section-header"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => toggleSection(section.title)}
              >
                <span className="wx-sidebar-section-title">{section.title}</span>
                <ChevronDown
                  size={14}
                  aria-hidden="true"
                  className={`wx-sidebar-chevron${open ? " wx-sidebar-chevron--open" : ""}`}
                />
              </button>
              {open && (
                <div className="wx-sidebar-section-items" id={panelId}>
                  {section.items.map((item) =>
                    item.to ? (
                      <Link
                        key={item.key}
                        to={item.to}
                        className={`wx-sidebar-item${isActivePath(location.pathname, item) ? " wx-sidebar-item--active" : ""}`}
                        aria-current={isActivePath(location.pathname, item) ? "page" : undefined}
                      >
                        <span className="wx-sidebar-item-mark" aria-hidden="true">
                          {item.mark}
                        </span>
                        <span>{item.label}</span>
                      </Link>
                    ) : (
                      <span
                        key={item.key}
                        className="wx-sidebar-item wx-sidebar-item--reserved"
                        title={item.reservedNote}
                      >
                        <span className="wx-sidebar-item-mark" aria-hidden="true">
                          {item.mark}
                        </span>
                        <span>{item.label}</span>
                      </span>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="wx-sidebar-footer">
        <span className="wx-sidebar-footer-label">Signed in as</span>
        <strong className="wx-sidebar-footer-value">
          {status === "ready" ? [data.primaryRole, scopeLabel].filter(Boolean).join(" · ") : "..."}
        </strong>
      </div>
    </aside>
  );
}
